import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { supplierCreateSchema, type Supplier, type SupplierCreateInput } from './types';
import { useCreateSupplier, useUpdateSupplier } from './useSuppliers';

interface SuppliersFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog runs in edit mode and PUTs `/companies/:id`. */
  row?: Supplier | null;
}

export const SuppliersFormDialog = ({ open, onOpenChange, row }: SuppliersFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const isEdit = !!row;

  const form = useForm<SupplierCreateInput>({
    resolver: zodResolver(supplierCreateSchema),
    defaultValues: { legal_name: '', display_name: '', registration_info: '' },
  });

  useEffect(() => {
    if (!open) {
      form.reset({ legal_name: '', display_name: '', registration_info: '' });
      return;
    }
    if (row) {
      form.reset({
        legal_name: row.legal_name,
        display_name: row.display_name,
        registration_info: row.registration_info ?? '',
      });
    }
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (isEdit && row) {
      await update.mutateAsync({ id: row.company_id, body: values });
    } else {
      await create.mutateAsync(values);
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('efs.masters.suppliers.edit', { ns: 'masters' })
              : t('efs.masters.suppliers.create', { ns: 'masters' })}
          </SheetTitle>
          <SheetDescription>
            {t('efs.masters.suppliers.create_subtitle', { ns: 'masters' })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.suppliers.fields.display_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="legal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.suppliers.fields.legal_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="registration_info"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.suppliers.fields.registration_info', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
